import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Badge, Spinner, Alert
} from 'react-bootstrap';
import { useOutletContext } from 'react-router-dom';
import { adminApi } from '../services/api';
import type { ActionLog } from '../types';

interface LayoutContext {
  refreshKey: number;
  onLogout: () => void;
  setLastUpdate: (d: Date) => void;
}

export const ActionLogsPage: React.FC = () => {
  const { refreshKey, onLogout, setLastUpdate } = useOutletContext<LayoutContext>();
  const [logs, setLogs] = useState<ActionLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const data = await adminApi.getRecentLogs(100);
      setLogs(data.logs);
      setLastUpdate(new Date());
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Session expirée. Reconnectez-vous.');
        setTimeout(() => onLogout(), 2000);
      } else {
        setError(err.message || 'Chargement impossible du journal');
      }
    } finally {
      setLoading(false);
    }
  }, [onLogout, setLastUpdate]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    if (refreshKey > 0) fetchData();
  }, [refreshKey, fetchData]);

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3 text-muted fw-semibold">Chargement du journal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="text-center">
        <Alert.Heading>
          <i className="bi bi-exclamation-triangle-fill me-2"></i>Erreur
        </Alert.Heading>
        <p>{error}</p>
      </Alert>
    );
  }

  return (
    <Card className="border-0 shadow-sm">
      <Card.Header className="bg-white border-0 pt-3">
        <h5 className="mb-0 fw-semibold fs-6">
          <i className="bi bi-journal-text me-2"></i>
          Dernières actions ({logs.length})
        </h5>
      </Card.Header>
      <Card.Body className="p-0">
        <div className="table-responsive" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          <Table hover className="mb-0">
            <thead className="table-light sticky-top">
              <tr>
                <th className="border-0">Action</th>
                <th className="border-0">Détails</th>
                <th className="border-0 text-center">Statut</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} className={!log.success ? 'table-danger' : ''}>
                  <td>
                    <Badge bg="secondary" className="px-2 py-1">
                      {log.action_type}
                    </Badge>
                  </td>
                  <td>
                    <span className="small text-muted">
                      {log.details || '--'}
                    </span>
                    {log.error_message && (
                      <div className="text-danger small mt-1">
                        <i className="bi bi-exclamation-circle me-1"></i>
                        {log.error_message.substring(0, 80)}{log.error_message.length > 80 ? '...' : ''}
                      </div>
                    )}
                  </td>
                  <td className="text-center">
                    {log.success ? (
                      <Badge bg="success" className="px-3">
                        <i className="bi bi-check-circle me-1"></i>
                        Réussie
                      </Badge>
                    ) : (
                      <Badge bg="danger" className="px-3">
                        <i className="bi bi-x-circle me-1"></i>
                        Échec
                      </Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
};
